import React, { useEffect, useState } from "react";
import "./horizonTable.css";
import { toast } from "react-toastify";
import {
  adminEditModalOverlayStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditLabelStyle,
  adminEditInputStyle,
  ADMIN_THEME,
} from "./adminEditModalTheme";

const appUpdateModalContentStyle = {
  border: "none",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
};

const appUpdateFieldStyle = {
  ...adminEditInputStyle,
  backgroundColor: "#fafafa",
  color: "#1f2937",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
};

const appUpdateInputIconStyle = {
  backgroundColor: "#fdf2f4",
  border: "1px solid #e2e8f0",
  borderRight: "none",
  color: ADMIN_THEME,
  minWidth: 42,
  justifyContent: "center",
};

function AppUpdatePage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState({ appVersion: "", url: "" });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageSize = 10;
  const normalizedRole = (currentUserRole || "").toLowerCase();
  const canAdd = normalizedRole === "admin";

  useEffect(() => {
    fetchItems();
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role) {
        setCurrentUserRole(user.role);
      }
    } catch (err) {
      console.error("Error getting user role:", err);
    }
  }, []);

  function closeAddModal() {
    setShowAddModal(false);
    setNewItem({ appVersion: "", url: "" });
  }

  function closeEditModal() {
    setShowEditModal(false);
    setEditItem(null);
  }

  function isValidUrl(val) {
    const s = (val || "").trim();
    if (!s) return false;
    try {
      const parsed = new URL(s);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  }

  async function fetchItems() {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please login again.");
        setLoading(false);
        return;
      }
      const res = await fetch(baseUrl + "/api/app-updates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) setError("Authentication failed. Please login again.");
        else if (res.status === 403) setError("You don't have permission to access this data.");
        else setError(`Failed to load app updates. Server responded with ${res.status}.`);
        setItems([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setItems(data.data);
        setError(null);
      } else {
        setItems([]);
        setError(data.message || "Failed to load app updates.");
      }
    } catch (err) {
      console.error("Error fetching app updates:", err);
      setItems([]);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  const handleAdd = async () => {
    if (!canAdd) {
      toast.error("You don't have permission to add app updates");
      return;
    }
    if (!newItem.appVersion || !newItem.appVersion.trim()) {
      toast.error("App version is required");
      return;
    }
    if (!newItem.url || !newItem.url.trim()) {
      toast.error("URL is required");
      return;
    }
    if (!isValidUrl(newItem.url)) {
      toast.error("Please enter a valid URL");
      return;
    }
    const addButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (addButton) {
      addButton.disabled = true;
      addButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Adding...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/app-updates", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newItem),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchItems();
        closeAddModal();
        toast.success("App update added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    } finally {
      if (addButton) {
        addButton.disabled = false;
        addButton.innerHTML = "Save";
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem({ ...item });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editItem.appVersion || !editItem.appVersion.trim()) {
      toast.error("App version is required");
      return;
    }
    if (!editItem.url || !editItem.url.trim()) {
      toast.error("URL is required");
      return;
    }
    if (!isValidUrl(editItem.url)) {
      toast.error("Please enter a valid URL");
      return;
    }
    const saveButton = document.querySelector('.modal-footer button[type="button"]:last-child');
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Saving...';
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/app-updates/${editItem._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ appVersion: editItem.appVersion, url: editItem.url }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchItems();
        closeEditModal();
        toast.success("App update saved!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.innerHTML = "Save";
      }
    }
  };

  const filtered = items.filter((item) => {
    const fields = [item.appVersion, item.url].map((val) => (val || "").toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every((word) => fields.some((field) => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered
    .slice()
    .reverse()
    .slice((page - 1) * pageSize, page * pageSize);
  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB");
  };

  const renderFormFields = (values, onChange) => (
    <div className="row g-3">
      <div className="col-12">
        <label style={adminEditLabelStyle}>App version</label>
        <div className="input-group">
          <span className="input-group-text" style={appUpdateInputIconStyle}>
            <i className="fa fa-code-branch" />
          </span>
          <input
            type="text"
            className="form-control"
            style={appUpdateFieldStyle}
            autoComplete="off"
            value={values.appVersion}
            onChange={(e) => onChange({ ...values, appVersion: e.target.value })}
            placeholder="e.g. 1.0.0"
          />
        </div>
      </div>
      <div className="col-12">
        <label style={adminEditLabelStyle}>URL</label>
        <div className="input-group">
          <span className="input-group-text" style={appUpdateInputIconStyle}>
            <i className="fa fa-link" />
          </span>
          <input
            type="url"
            className="form-control"
            style={appUpdateFieldStyle}
            autoComplete="off"
            value={values.url}
            onChange={(e) => onChange({ ...values, url: e.target.value })}
            placeholder="https://example.com/app.apk"
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>App update</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i
                className="fa fa-search"
                style={{ position: "absolute", left: "12px", color: "#8b1634", fontSize: "14px" }}
              />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  outline: "none",
                  width: "250px",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b1634")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>
            {canAdd && items.length === 0 && (
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  fontWeight: "600",
                  padding: "10px 20px",
                  borderRadius: "12px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
              >
                <i className="fa fa-plus" /> Add
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            overflowX: "auto",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 1fr 1fr 1.2fr",
              background: "#f7fafc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>App version</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>URL</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Created Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Updated Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Actions</div>
          </div>

          <div>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #8b1634",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "20px",
                  }}
                />
                <div style={{ color: "#8b1634", fontSize: "16px", fontWeight: "600" }}>Loading app updates...</div>
              </div>
            ) : error ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#e53e3e", marginBottom: "16px" }}>⚠️</div>
                <div style={{ color: "#e53e3e", fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}>
                  Error Loading Data
                </div>
                <div style={{ color: "#718096", fontSize: "14px", textAlign: "center", maxWidth: "400px", marginBottom: "20px" }}>
                  {error}
                </div>
                <button
                  onClick={fetchItems}
                  style={{
                    background: "#8b1634",
                    color: "#fff",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6d1128")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#8b1634")}
                >
                  <i className="fa fa-refresh" /> Try Again
                </button>
              </div>
            ) : paged.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "48px", color: "#cbd5e0", marginBottom: "16px" }}>📱</div>
                <div style={{ color: "#718096", fontSize: "16px", fontWeight: "600" }}>No app updates found</div>
                {search && (
                  <div style={{ color: "#a0aec0", fontSize: "14px", marginTop: "8px" }}>
                    Try adjusting your search criteria
                  </div>
                )}
              </div>
            ) : (
              paged.map((item, idx) => (
                <div
                  key={item._id || idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr 1fr 1fr 1.2fr",
                    padding: "16px",
                    borderBottom: "1px solid #f1f5f9",
                    gap: "16px",
                    alignItems: "center",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{item.appVersion || "-"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px", wordBreak: "break-all" }}>
                    {item.url ? (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: "#8b1634" }}>
                        {item.url}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDateTime(item.createdAt)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDateTime(item.updatedAt)}</div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      onClick={() => handleEdit(item)}
                      title="Edit"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        padding: "6px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: "16px" }} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {showAddModal && (
          <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={appUpdateModalContentStyle}>
                <div className="modal-header" style={adminEditModalHeaderStyle}>
                  <h5 className="modal-title" style={adminEditModalTitleStyle}>
                    <i className="fa fa-plus" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }} />
                    Add App update
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeAddModal} />
                </div>
                <div className="modal-body" style={adminEditModalBodyStyle}>
                  {renderFormFields(newItem, setNewItem)}
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                  <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={closeAddModal}>
                    Cancel
                  </button>
                  <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleAdd}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEditModal && editItem && (
          <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={appUpdateModalContentStyle}>
                <div className="modal-header" style={adminEditModalHeaderStyle}>
                  <h5 className="modal-title" style={adminEditModalTitleStyle}>
                    <i className="fa fa-edit" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }} />
                    Edit App update
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={closeEditModal} />
                </div>
                <div className="modal-body" style={adminEditModalBodyStyle}>
                  {renderFormFields(editItem, setEditItem)}
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                  <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginTop: "18px",
              gap: "12px",
            }}
          >
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === 1 ? "#f7fafc" : "#ffffff",
                color: page === 1 ? "#a0aec0" : "#8b1634",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Prev
            </button>
            <span style={{ fontWeight: "700", color: "#8b1634", fontSize: "16px" }}>
              {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: page === totalPages ? "#f7fafc" : "#ffffff",
                color: page === totalPages ? "#a0aec0" : "#8b1634",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default AppUpdatePage;
